// app/src/lib/ocr/uploader.js — receipt image → S3 → OCR pipeline.
//
// Path security: the Lambda issues a presigned PUT scoped to {userId}/{uuid}.
// The client does NOT choose the key (preventing path traversal).

import { getAccessToken } from '../auth/cognitoClient.js';
import { getConfig } from '../config.js';
import { emitOcrFailed } from '../alerts/index.js';

/**
 * @typedef {Object} UploadResult
 * @property {string} receiptId
 * @property {string} objectKey
 */

/**
 * Request a presigned PUT, upload the image, return the receiptId.
 * Caller is responsible for compressing the image to a reasonable size first.
 *
 * @param {Blob} blob
 * @param {string} mimeType - "image/jpeg" | "image/png" | "application/pdf".
 * @returns {Promise<UploadResult>}
 */
export async function uploadReceiptImage(blob, mimeType, opts = {}) {
  if (!(blob instanceof Blob)) throw new Error('upload: not a Blob');
  if (!/^(image\/(jpeg|png|webp)|application\/pdf)$/.test(mimeType)) {
    throw new Error(`upload: disallowed content-type ${mimeType}`);
  }
  if (blob.size > 20 * 1024 * 1024) throw new Error('upload: blob > 20MB');

  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) throw new Error('upload: no access token');

  // `kind:'delivery'` routes the server to the delivery-statement path (Pro,
  // PDF-only, separate OCR prompt + output).
  const reqBody = { contentType: mimeType, byteLength: blob.size };
  if (opts.kind) reqBody.kind = opts.kind;
  const presign = await fetch(`${cfg.apiBaseUrl}/receipts/presign`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' },
    body: JSON.stringify(reqBody),
  });
  if (!presign.ok) {
    // Surface the server's reason (e.g. ZIP_REQUIRED, quota) so the UI can guide
    // the user instead of showing a generic failure.
    let info = null;
    try { info = await presign.json(); } catch { /* non-JSON error body */ }
    const e = new Error((info && info.error) || `upload: presign ${presign.status}`);
    e.status = presign.status;
    if (info && info.code) e.code = info.code;
    throw e;
  }
  const { url, receiptId, objectKey, requiredHeaders } = await presign.json();

  const putRes = await fetch(url, {
    method: 'PUT',
    headers: { 'content-type': mimeType, ...(requiredHeaders || {}) },
    body: blob,
  });
  if (!putRes.ok) {
    if (!navigator.onLine) {
      await queueForRetry(blob, mimeType, receiptId, objectKey, url);
      return { receiptId, objectKey, queued: true };
    }
    throw new Error(`upload: PUT ${putRes.status}`);
  }
  return { receiptId, objectKey };
}

/**
 * Poll for an OCR result with exponential backoff. While processing, the server
 * returns 200 `{status:'processing'}` (older deploys returned 404) — both mean
 * "keep waiting". A 200 with the actual result ends the poll.
 */
export async function pollOcrResult(receiptId, { timeoutMs = 60_000, kind } = {}) {
  const cfg = getConfig();
  const at = await getAccessToken();
  const start = Date.now();
  let delay = 1500;
  const q = kind ? `?kind=${encodeURIComponent(kind)}` : '';
  while (Date.now() - start < timeoutMs) {
    const res = await fetch(`${cfg.apiBaseUrl}/receipts/${encodeURIComponent(receiptId)}/ocr${q}`, {
      headers: { authorization: `Bearer ${at}` },
    });
    if (res.status === 200) {
      const body = await res.json().catch(() => null);
      if (body && body.status === 'processing') { /* not ready — keep polling */ }
      else if (body) return body;
    } else if (res.status !== 404) {
      throw new Error(`ocr: poll ${res.status}`);
    }
    await new Promise((r) => setTimeout(r, delay));
    delay = Math.min(delay * 1.5, 8000);
  }
  // §12 OCR_FAILED alert: terminal failure to deliver an OCR result.
  try { await emitOcrFailed(receiptId, 'OCR did not complete within the polling window.'); }
  catch { /* never let alerting failure mask the original timeout */ }
  throw new Error('ocr: timeout');
}

/**
 * Offline queue: stash the blob + the exact PUT URL the SW must hit on replay.
 * Background Sync tag registered after queueing so Chromium can wake the SW
 * on connectivity return.
 */
async function queueForRetry(blob, mimeType, receiptId, objectKey, targetUrl) {
  const cache = await caches.open('kanjo-upload-queue-v1');
  const req = new Request(`/upload-queue/${receiptId}`, { method: 'POST' });
  await cache.put(req, new Response(blob, {
    headers: { 'content-type': mimeType, 'x-target-url': targetUrl, 'x-object-key': objectKey },
  }));
  try {
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      const reg = await navigator.serviceWorker.ready;
      await reg.sync.register('kanjo-receipts-upload');
    }
  } catch { /* Safari/FF: page may also listen for `online` and replay. */ }
}
